import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setUserProperties,
} from 'firebase/analytics';
import type { IAnalyticsService } from '@breath-lab/services';
import { AnalyticsEvent, AnalyticsParam } from '@breath-lab/domain';
import { firebaseApp } from '../firebase/firebaseConfig';
import { webContentGroupForPath } from '../../domain/webContentGroup';

/**
 * Events are always sent. Storage (cookies) is governed by GA4 Consent Mode,
 * not by short-circuiting here: pre-consent and "essential" sessions send
 * cookieless pings (no cookies, source attribution preserved); "accept all"
 * grants persistent storage via `applyAnalyticsConsent`. Do NOT re-add a
 * localStorage consent gate — it drops the cookieless pings and breaks
 * acquisition attribution (sessions collapse to Direct/Unassigned).
 */
export class WebAnalyticsService implements IAnalyticsService {
  private analyticsPromise: Promise<Analytics | null> | null = null;

  private getAnalyticsInstance(): Promise<Analytics | null> {
    if (!this.analyticsPromise) {
      this.analyticsPromise = isSupported().then((supported) =>
        supported ? getAnalytics(firebaseApp) : null,
      );
    }
    return this.analyticsPromise;
  }

  async logEvent(eventName: string, params?: Record<string, unknown>): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) return;
    let finalParams = params;
    if (eventName === AnalyticsEvent.screenView && typeof window !== 'undefined') {
      finalParams = {
        [AnalyticsParam.contentGroup]: webContentGroupForPath(window.location.pathname),
        ...params,
      };
    }
    firebaseLogEvent(analytics, eventName, finalParams);
  }

  async logScreenView(screenName: string, screenClass?: string): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) return;
    firebaseLogEvent(analytics, 'screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenClass ?? screenName,
    });
  }

  async setUserProperty(name: string, value: string): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) return;
    setUserProperties(analytics, { [name]: value });
  }

  /** No-op on web: collection is governed by the cookie consent banner, not a
   *  per-user setting. Present to satisfy the shared IAnalyticsService. */
  async setAnalyticsCollectionEnabled(): Promise<void> {}
}
